import { NotCustomerRoles } from "entities/UserRole";
import React, { useState } from "react";
import { usePermissions, useTranslate } from "react-admin";

import { useExportColumns } from "../../../hooks/useExportColumns";
import { useExportColumnsSelection } from "../../../hooks/useExportColumnsSelection";
import { CompanyFilterField } from "../ExportDialog/CompanyFilterField";
import { ExportColumnsButton } from "../ExportDialog/ExportColumnsButton";
import { ExportModalBase } from "../ExportDialog/ExportModalBase";
import { ExportTriggerButton } from "../ExportDialog/ExportTriggerButton";
import { useSimpleModal } from "../useSimpleModal";

export const InventoryListExportModal = ({ filterValues = {} }) => {
	const exportModal = useSimpleModal();
	const { isLoading, columns, columnsChoices, loadColumns } = useExportColumns(
		"inventory-list-export-header"
	);
	const [headerFilter, setHeaderFilter] = useState({});

	const onOpen = () => {
		exportModal.handleOpen();
	};

	React.useEffect(() => {
		if (exportModal.opened) {
			loadColumns(headerFilter);
		}
	}, [headerFilter]);

	return (
		<React.Fragment>
			<ExportTriggerButton label="osfc.buttons.export" isLoading={isLoading} onClick={onOpen} />
			{exportModal.opened && (
				<InventoryListExportView
					viewComponentProps={{ onModalClose: exportModal.handleClose }}
					columns={columns}
					setHeaderFilter={setHeaderFilter}
					columnsChoices={columnsChoices}
					filterValues={filterValues}
				/>
			)}
		</React.Fragment>
	);
};

export const InventoryListExportView = ({
	viewComponentProps,
	columnsChoices,
	columns,
	setHeaderFilter,
	filterValues = {},
	ViewComponent = ExportModalBase,
}) => {
	const tableRef = React.useRef(null);
	const translate = useTranslate();
	const { permissions } = usePermissions();
	const [companyId, setCompanyId] = React.useState(undefined);
	const { columnsSelected, setColumnsSelected, excludedColumns } =
		useExportColumnsSelection(columns);

	const isNotCustomer = NotCustomerRoles.includes(permissions?.role);

	const endpoint = "inventory-list";

	const filters = React.useMemo(() => {
		return {
			company_id: companyId,
			as_of_date: filterValues.as_of_date,
			is_expiration_report: filterValues.is_expiration_report || undefined,
			excluded_columns: excludedColumns,
		};
	}, [companyId, filterValues.as_of_date, filterValues.is_expiration_report, excludedColumns]);

	React.useEffect(() => {
		setHeaderFilter({
			company_id: companyId,
		});
	}, [companyId, setHeaderFilter]);

	const titleButtons = [
		isNotCustomer && (
			<CompanyFilterField
				value={companyId}
				onChange={setCompanyId}
				label={translate("resources.inventory-list.export-dialog.fields.company_id")}
			/>
		),
		<ExportColumnsButton
			choices={columnsChoices}
			value={columnsSelected}
			onChange={setColumnsSelected}
		/>,
	];

	return (
		<ViewComponent
			filters={filters}
			endpoint={endpoint}
			tableRef={tableRef}
			{...viewComponentProps}
			titleButtons={titleButtons}
		/>
	);
};
